import type {
  CashLocationSchema,
  CashLocationSchemaWithId,
  MutationSchema,
} from "@repo/validators";
import type { z } from "zod";
import ReceiptPrinterEncoder from "@point-of-sale/receipt-printer-encoder";
import { ClockTypes, Permissions, ReceiptType } from "@repo/database";
import { capitalize, formatIntToCurrency } from "@repo/utils";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type {
  CashLocation,
  PartialCashLocationMutation,
} from "~/providers/cash-location-provider";
import type { ManagerResult } from "~/types";
import { useCashLocation } from "~/hooks/use-cash-location";
import { useConfiguration } from "~/hooks/use-configuration";
import { usePrinter } from "~/hooks/use-printer";
import { useUser } from "~/hooks/use-user";
import { api } from "~/lib/api";
import { useCashLocationStore } from "~/stores/use-cash-location-store";
import { usePermissionManager } from "./use-permission-manager";

export const useCashLocationManager = () => {
  const { t } = useTranslation("common");

  const { user } = useUser();

  const { cashLocations, cashLocationMutationCategories } = useCashLocation();

  const { t: userT } = useTranslation("common", { lng: user.language });

  const { hasPermission } = usePermissionManager();

  const { receiptSettings } = useConfiguration();

  const { printers, printRoutes, sendToPrinter, getPrinterLanguage } =
    usePrinter();

  const { setCashLocationDialogOpen, setSelectedCashLocation } =
    useCashLocationStore();

  const { handleCashLocationUpdate } = useCashLocationUpdates();

  const { mutate: createCashLocation } =
    api.cashLocation.createCashLocation.useMutation({
      async onSuccess(result) {
        toast.success(
          capitalize(
            t("create_item_success", { item: t("CashLocation.cash_location") }),
          ),
        );

        await handleCashLocationUpdate(result);
      },
    });

  const { mutate: updateCashLocation } =
    api.cashLocation.updateCashLocation.useMutation({
      onSuccess() {
        toast.success(
          capitalize(
            t("update_item_success", { item: t("CashLocation.cash_location") }),
          ),
        );
      },
    });

  const { mutate: createMutation } =
    api.cashLocation.createMutation.useMutation({
      onSuccess() {
        toast.success(
          t("create_item_success", { item: t("Mutation.mutation") }),
        );
      },
    });

  const createCashLocationHandler = async (
    data: z.infer<ReturnType<typeof CashLocationSchema>>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_section,
      label: t("Permissions.create_section_label"),
    });

    return new Promise<
      ManagerResult<keyof z.infer<ReturnType<typeof CashLocationSchema>>>
    >((resolve) => {
      if (!permission)
        return resolve({
          error: { path: "root", message: t("Permissions.no_permission") },
        });

      createCashLocation(data, {
        onSuccess() {
          return resolve({
            data: true,
          });
        },
        onError() {
          return resolve({
            error: {
              path: "root",
              message: capitalize(
                t("create_item_error", {
                  item: t("CashLocation.cash_location"),
                }),
              ),
            },
          });
        },
      });
    });
  };

  const updateCashLocationHandler = async (
    data: z.infer<ReturnType<typeof CashLocationSchemaWithId>>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_section,
      label: t("Permissions.update_section_label"),
    });

    return new Promise<
      ManagerResult<keyof z.infer<ReturnType<typeof CashLocationSchemaWithId>>>
    >((resolve) => {
      if (!permission)
        return resolve({
          error: { path: "root", message: t("Permissions.no_permission") },
        });

      updateCashLocation(data, {
        onSuccess() {
          return resolve({
            data: true,
          });
        },
        onError() {
          resolve({
            error: {
              path: "root",
              message: capitalize(
                t("update_item_error", { item: t("section") }),
              ),
            },
          });
        },
      });
    });
  };

  const createMutationHandler = async (
    data: z.infer<ReturnType<typeof MutationSchema>>,
  ) => {
    return new Promise<
      ManagerResult<z.infer<ReturnType<typeof MutationSchema>>>
    >((resolve) => {
      createMutation(data, {
        onSuccess() {
          const cashLocation = cashLocations.find(
            (location) => location.id === data.cashLocationId,
          )?.name;

          const category = cashLocationMutationCategories.find(
            (category) => category.id === data.category,
          )?.name;

          printMutation({
            createdAt: new Date(),
            comment: data.description,
            value: data.value,
            type: data.type,
            category: category ?? "",
            vatRate: data.vatRate ?? 0,
            cashLocation: cashLocation ?? "",
          });
          resolve({
            data: true,
          });
        },
        onError() {
          resolve({
            error: {
              path: "root",
              message: capitalize(
                t("create_item_error", { item: t("Mutation.mutation") }),
              ),
            },
          });
        },
      });
    });
  };

  const printMutation = (mutation: PartialCashLocationMutation) => {
    const settings = receiptSettings.find(
      (setting) => setting.type === ReceiptType.pro_forma,
    );

    const printRoute = printRoutes.find(
      (pr) => pr.id === settings?.printRouteId,
    );

    if (!printRoute) {
      throw new Error("Print route not found");
    }

    for (const printer of printers.filter((p) =>
      p.printRoutes.includes(printRoute.id),
    )) {
      const width = printer.width;

      const encoder = new ReceiptPrinterEncoder({
        language: getPrinterLanguage(printer.language),
        columns: width,
        imageMode: printer.imageMode,
      });

      const result = encoder
        .initialize()
        .codepage("windows1252")
        .align("center");

      result.text("");

      result
        .height(2)
        .width(2)
        .bold(true)
        .line("Mutation")
        .newline()
        .bold(false)
        .width(1)
        .height(1)
        .line(
          userT("Ticket.time", {
            time: format(
              mutation.createdAt,
              user.clock === ClockTypes.military
                ? "dd-MM-yyyy HH:mm"
                : "MM/dd/yyyy hh:aa a",
            ),
          }),
        );
      result.rule();

      const firstColumnWidth = Math.round(width * 0.1);
      const secondColumnWidth = Math.round(width * 0.25);

      result.table(
        [
          {
            width: firstColumnWidth,
            align: "left",
            marginRight: 1,
          },
          {
            width: secondColumnWidth,
            align: "left",
          },
          {
            width: width - firstColumnWidth - secondColumnWidth - 1,
            align: "right",
          },
        ],
        [
          [
            mutation.type,
            formatIntToCurrency(mutation.value, true),
            `${mutation.category} ${mutation.comment ? `(${mutation.comment})` : ""}`,
          ],
        ],
      );

      // if (employeeName) {
      //   result.line(
      //     userT("Ticket.employee", {
      //       employee_name: employeeName,
      //     }),
      //   );
      // }

      result.newline();
      result.newline();
      result.newline();
      result.newline();
      result.newline();
      result.newline();
      result.newline();

      result.cut("partial");

      const encoded = result.encode();

      void sendToPrinter(encoded, printer);
    }
  };

  const openCashLocationDialog = (cashLocation?: CashLocation) => {
    // const { permission } = await hasPermission({
    //   requiredPermission: Permissions.,
    //   label: t("Permissions.create_section_label"),
    // });
    // if (!permission) return;

    setSelectedCashLocation(cashLocation);
    setCashLocationDialogOpen(true);
  };

  return {
    createCashLocation: createCashLocationHandler,
    updateCashLocation: updateCashLocationHandler,
    openCashLocationDialog,
    createMutation: createMutationHandler,
  };
};

export const useCashLocationUpdates = () => {
  const utils = api.useUtils();

  const handleCashLocationUpdate = async (sectionId: string) => {
    const result = await utils.section.getById.fetch(sectionId);

    void utils.section.getAll.setData(undefined, (old) => {
      if (!old) return old;
      return [...old.filter((p) => p.id !== result.id), result];
    });
  };

  const handleCashLocationDelete = (sectionId: string) => {
    void utils.section.getAll.setData(undefined, (old) => {
      if (!old) return old;
      return old.filter((p) => p.id !== sectionId);
    });
  };

  return {
    handleCashLocationUpdate,
    handleCashLocationDelete,
  };
};
